import { useCallback, useRef, useState } from 'react';
import type {
    ChangeEvent,
    ClipboardEvent,
    FormEventHandler,
    KeyboardEvent,
    ReactNode,
} from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import type { MfaMethod } from '@pillar-portal/api-types';
import { Mail, MessageSquare, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export const MfaChallengeMethodLabels: Record<MfaMethod, string> = {
    TOTP: 'Authenticator app',
    EMAIL: 'Email',
    SMS: 'Text message',
};

const MfaChallengeMethodDescriptions: Record<MfaMethod, string> = {
    TOTP: 'Use a code from your authenticator.',
    EMAIL: 'Send a code to your verified email address.',
    SMS: 'Send a code by text message.',
};

export interface MfaChallengeDeliveryAction {
    readonly label: string;
    readonly pendingLabel: string;
    readonly isPending: boolean;
    readonly isDisabled: boolean;
    readonly onClick: () => void;
}

export interface MfaChallengeTotpSetupAction {
    readonly qrCode: string | null;
    readonly isPending: boolean;
    readonly isDisabled: boolean;
    readonly onStart: () => void;
}

export interface MfaChallengeSecondaryAction {
    readonly label: string;
    readonly isDisabled: boolean;
    readonly onClick: () => void;
}

export interface MfaChallengeCardProps {
    readonly title: string;
    readonly contextMessage?: string;
    readonly availableMethods: readonly MfaMethod[];
    readonly selectedMethod: MfaMethod;
    readonly onSelectMethod: (method: MfaMethod) => void;
    readonly areMethodsDisabled: boolean;
    readonly deliveryAction?: MfaChallengeDeliveryAction;
    readonly totpSetupAction?: MfaChallengeTotpSetupAction;
    readonly codeInputProps: UseFormRegisterReturn<'totp_code'>;
    readonly codeInputDisabled: boolean;
    readonly codeError?: string;
    readonly errorMessage?: string;
    readonly successMessage?: string;
    readonly submitLabel: string;
    readonly submitPendingLabel: string;
    readonly isSubmitPending: boolean;
    readonly isSubmitDisabled: boolean;
    readonly secondaryAction?: MfaChallengeSecondaryAction;
    readonly onSubmit: FormEventHandler<HTMLFormElement>;
}

export interface MfaChallengeLoadingCardProps {
    readonly message: string;
}

const MfaCodeDigitCount = 6;

interface MfaCodeInputProps {
    readonly label: string;
    readonly codeInputProps: UseFormRegisterReturn<'totp_code'>;
    readonly disabled: boolean;
}

function normalizeMfaCode(value: string): string {
    return value.replace(/\D/g, '').slice(0, MfaCodeDigitCount);
}

function MethodIcon({ method }: { readonly method: MfaMethod }) {
    const className = 'h-5 w-5';
    if (method === 'EMAIL') {
        return <Mail aria-hidden="true" className={className} />;
    }
    if (method === 'SMS') {
        return <MessageSquare aria-hidden="true" className={className} />;
    }
    return <ShieldCheck aria-hidden="true" className={className} />;
}

function MfaCodeInput({ label, codeInputProps, disabled }: MfaCodeInputProps) {
    const [code, setCode] = useState('');
    const hiddenInputRef = useRef<HTMLInputElement | null>(null);
    const digitInputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const digits = Array.from({ length: MfaCodeDigitCount }, (_, index) => code[index] ?? '');

    const setHiddenInputRef = useCallback(
        (element: HTMLInputElement | null): void => {
            hiddenInputRef.current = element;
            codeInputProps.ref(element);
        },
        [codeInputProps]
    );

    const updateRegisteredCode = useCallback(
        (nextCode: string): void => {
            setCode(nextCode);
            if (hiddenInputRef.current !== null) {
                hiddenInputRef.current.value = nextCode;
                const changeResult = codeInputProps.onChange({
                    target: hiddenInputRef.current,
                    type: 'change',
                });
                if (changeResult instanceof Promise) {
                    changeResult.catch(() => undefined);
                }
            }
        },
        [codeInputProps]
    );

    const focusDigit = useCallback((index: number): void => {
        digitInputRefs.current[index]?.focus();
    }, []);

    const updateDigitsFromIndex = useCallback(
        (startIndex: number, rawValue: string): void => {
            const normalizedValue = normalizeMfaCode(rawValue);
            if (normalizedValue.length === 0) {
                const nextDigits = digits.slice();
                nextDigits[startIndex] = '';
                updateRegisteredCode(nextDigits.join(''));
                return;
            }

            const nextDigits = digits.slice();
            normalizedValue.split('').forEach((digit, offset) => {
                const index = startIndex + offset;
                if (index < MfaCodeDigitCount) {
                    nextDigits[index] = digit;
                }
            });
            updateRegisteredCode(nextDigits.join(''));
            focusDigit(Math.min(startIndex + normalizedValue.length, MfaCodeDigitCount - 1));
        },
        [digits, focusDigit, updateRegisteredCode]
    );

    const handleDigitChange = useCallback(
        (index: number, event: ChangeEvent<HTMLInputElement>): void => {
            updateDigitsFromIndex(index, event.target.value);
        },
        [updateDigitsFromIndex]
    );

    const handleDigitKeyDown = useCallback(
        (index: number, event: KeyboardEvent<HTMLInputElement>): void => {
            if (event.key !== 'Backspace') {
                return;
            }

            event.preventDefault();
            const nextDigits = digits.slice();
            if (nextDigits[index]) {
                nextDigits[index] = '';
                updateRegisteredCode(nextDigits.join(''));
                return;
            }

            if (index > 0) {
                nextDigits[index - 1] = '';
                updateRegisteredCode(nextDigits.join(''));
                focusDigit(index - 1);
            }
        },
        [digits, focusDigit, updateRegisteredCode]
    );

    const handleDigitPaste = useCallback(
        (index: number, event: ClipboardEvent<HTMLInputElement>): void => {
            event.preventDefault();
            updateDigitsFromIndex(index, event.clipboardData.getData('text'));
        },
        [updateDigitsFromIndex]
    );

    return (
        <div className="space-y-2">
            <Label id="totp_code_label" htmlFor="totp_code">
                {label}
            </Label>
            <input
                id="totp_code"
                type="hidden"
                name={codeInputProps.name}
                value={code}
                ref={setHiddenInputRef}
                disabled={disabled}
                readOnly
            />
            <div
                className="grid grid-cols-6 gap-2 sm:gap-3"
                role="group"
                aria-labelledby="totp_code_label"
            >
                {digits.map((digit, index) => (
                    <Input
                        key={index}
                        ref={(element) => {
                            digitInputRefs.current[index] = element;
                        }}
                        type="text"
                        inputMode="numeric"
                        autoComplete={index === 0 ? 'one-time-code' : 'off'}
                        placeholder="-"
                        maxLength={1}
                        value={digit}
                        aria-label={`${label} digit ${index + 1}`}
                        className="aspect-square h-auto min-h-14 min-w-0 rounded-lg text-center font-mono text-2xl"
                        disabled={disabled}
                        onBlur={codeInputProps.onBlur}
                        onChange={(event) => handleDigitChange(index, event)}
                        onKeyDown={(event) => handleDigitKeyDown(index, event)}
                        onPaste={(event) => handleDigitPaste(index, event)}
                    />
                ))}
            </div>
        </div>
    );
}

export function MfaChallengeLoadingCard({ message }: MfaChallengeLoadingCardProps) {
    return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
            <Card className="w-full max-w-md border-slate-200 shadow-lg">
                <CardContent className="py-8">
                    <div className="flex flex-col items-center justify-center gap-4">
                        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
                        <p className="text-sm text-muted-foreground">{message}</p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export function MfaChallengeCard({
    title,
    contextMessage,
    availableMethods,
    selectedMethod,
    onSelectMethod,
    areMethodsDisabled,
    deliveryAction,
    totpSetupAction,
    codeInputProps,
    codeInputDisabled,
    codeError,
    errorMessage,
    successMessage,
    submitLabel,
    submitPendingLabel,
    isSubmitPending,
    isSubmitDisabled,
    secondaryAction,
    onSubmit,
}: MfaChallengeCardProps) {
    const selectedMethodLabel = MfaChallengeMethodLabels[selectedMethod];

    const selectedMethodDetails: ReactNode = (() => {
        if (selectedMethod === 'TOTP' && totpSetupAction !== undefined) {
            if (totpSetupAction.qrCode !== null) {
                return (
                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                        <img
                            src={totpSetupAction.qrCode}
                            alt="Authenticator app QR code"
                            className="mx-auto h-48 w-48"
                        />
                        <p className="mt-3 text-center text-sm text-muted-foreground">
                            Scan the QR code, then enter the generated code.
                        </p>
                    </div>
                );
            }

            return (
                <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-center"
                    onClick={totpSetupAction.onStart}
                    disabled={totpSetupAction.isDisabled || totpSetupAction.isPending}
                >
                    {totpSetupAction.isPending ? 'Preparing...' : 'Set up authenticator app'}
                </Button>
            );
        }

        if ((selectedMethod === 'EMAIL' || selectedMethod === 'SMS') && deliveryAction) {
            return (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <div className="mb-3 text-sm text-slate-700">
                        We will send a 6-digit code to your email.
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-center bg-white"
                        onClick={deliveryAction.onClick}
                        disabled={deliveryAction.isDisabled || deliveryAction.isPending}
                    >
                        {deliveryAction.isPending
                            ? deliveryAction.pendingLabel
                            : deliveryAction.label}
                    </Button>
                </div>
            );
        }

        return null;
    })();

    return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
            <Card className="w-full max-w-xl border-slate-200 shadow-lg">
                <CardHeader className="space-y-3 text-center">
                    <CardTitle className="text-3xl font-semibold leading-tight">
                        {title}
                    </CardTitle>
                    {contextMessage && (
                        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                            {contextMessage}
                        </p>
                    )}
                </CardHeader>
                <CardContent>
                    <form onSubmit={onSubmit} className="space-y-6">
                        <section className="space-y-3" aria-labelledby="mfa-method-heading">
                            <h2
                                id="mfa-method-heading"
                                className="text-sm font-semibold text-slate-900"
                            >
                                Step 1
                            </h2>
                            <p className="text-sm font-normal text-slate-600">
                                Choose a method, then send a code to continue.
                            </p>
                            <div className="grid gap-3">
                                {availableMethods.map((method) => {
                                    const isSelected = selectedMethod === method;
                                    return (
                                        <button
                                            key={method}
                                            type="button"
                                            aria-label={MfaChallengeMethodLabels[method]}
                                            aria-pressed={isSelected}
                                            className={cn(
                                                'flex min-h-20 w-full items-center gap-3 rounded-lg border p-4 text-left transition-colors disabled:opacity-50',
                                                isSelected
                                                    ? 'border-blue-400 bg-blue-50'
                                                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                                            )}
                                            onClick={() => onSelectMethod(method)}
                                            disabled={areMethodsDisabled}
                                        >
                                            <span
                                                className={cn(
                                                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full border',
                                                    isSelected
                                                        ? 'border-blue-500 bg-white text-blue-700'
                                                        : 'border-slate-200 bg-slate-50 text-slate-600'
                                                )}
                                            >
                                                <MethodIcon method={method} />
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block text-sm font-semibold text-slate-950">
                                                    {MfaChallengeMethodLabels[method]}
                                                </span>
                                                <span className="mt-1 block text-sm text-slate-600">
                                                    {MfaChallengeMethodDescriptions[method]}
                                                </span>
                                            </span>
                                            <span
                                                aria-hidden="true"
                                                className={cn(
                                                    'h-5 w-5 shrink-0 rounded-full border',
                                                    isSelected
                                                        ? 'border-blue-600 bg-blue-600 shadow-[inset_0_0_0_4px_white]'
                                                        : 'border-slate-300 bg-white'
                                                )}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                            {selectedMethodDetails}
                        </section>

                        <section className="space-y-3" aria-labelledby="mfa-code-heading">
                            <h2
                                id="mfa-code-heading"
                                className="text-sm font-semibold text-slate-900"
                            >
                                Step 2
                            </h2>
                            <MfaCodeInput
                                label={`${selectedMethodLabel} code`}
                                codeInputProps={codeInputProps}
                                disabled={codeInputDisabled}
                            />
                            {codeError && <p className="text-sm text-destructive">{codeError}</p>}
                        </section>

                        {errorMessage && (
                            <div className="rounded-lg bg-destructive/15 p-3 text-sm text-destructive">
                                {errorMessage}
                            </div>
                        )}
                        {successMessage && (
                            <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">
                                {successMessage}
                            </div>
                        )}

                        <div className="space-y-3">
                            <Button type="submit" className="w-full" disabled={isSubmitDisabled}>
                                {isSubmitPending ? submitPendingLabel : submitLabel}
                            </Button>
                            {secondaryAction && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="w-full"
                                    onClick={secondaryAction.onClick}
                                    disabled={secondaryAction.isDisabled}
                                >
                                    {secondaryAction.label}
                                </Button>
                            )}
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
